import {useCallback, useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import CircleIcon from "@mui/icons-material/Circle";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";

import DataTable from "../../components/DataTable";
import EnhancedTable from "../../components/EnhancedTable";
import {
    getMyNotifications,
    markAllRead,
    markRead as apiMarkRead,
} from "../../api/notification/notificationApiClient";
import {useNotifications} from "../../context/NotificationProvider";
import {handleApiError, showSuccess} from "../../utils/notificationUtils";

/**
 * Full-page notifications list. Paginated via the existing DataTable.
 * Each row shows: status dot, title, body preview, type, received at,
 * and a "mark read" action when still unread.
 */
function Notifications() {
    const {refreshUnreadCount, decrementUnread, clearUnread} = useNotifications();
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [unreadOnly, setUnreadOnly] = useState(false);

    const load = useCallback(async () => {
        try {
            setIsLoading(true);
            const page = await getMyNotifications({page: 0, size: 100, unreadOnly});
            setItems(page?.content || []);
        } catch (err) {
            handleApiError(err, "Failed to load notifications");
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [unreadOnly]);

    useEffect(() => {
        load().then();
    }, [load]);

    const handleMarkOne = async (row) => {
        try {
            const updated = await apiMarkRead(row.id);
            setItems((prev) => prev.map((n) => (n.id === row.id ? updated : n)));
            decrementUnread();
        } catch (err) {
            handleApiError(err, "Failed to mark as read");
        }
    };

    const handleMarkAll = async () => {
        try {
            const updated = await markAllRead();
            showSuccess(`Marked ${updated} as read`);
            clearUnread();
            await load();
            await refreshUnreadCount();
        } catch (err) {
            handleApiError(err, "Failed to mark all as read");
        }
    };

    // The backend formats the timestamp in the branch's timezone and sends it
    // ready to display, so every client shows the same wall-clock time. Only
    // fall back to local conversion for older rows that predate that field.
    const formatDate = (row) => {
        if (row?.createdAtDisplay) return row.createdAtDisplay;
        if (!row?.createdAt) return "";
        try {
            return new Date(row.createdAt).toLocaleString();
        } catch {
            return row.createdAt;
        }
    };

    const columns = [
        {
            name: "",
            value: "status",
            align: "center",
            width: "5",
            renderCell: (row) =>
                row.status === "UNREAD" ? (
                    <CircleIcon sx={{fontSize: 10, color: "primary.main"}} />
                ) : (
                    <SoftBox />
                ),
        },
        {
            name: "Title",
            value: "title",
            align: "left",
            width: "20",
            renderCell: (row) => (
                <SoftTypography
                    variant="button"
                    fontWeight={row.status === "UNREAD" ? "bold" : "medium"}
                    color="dark"
                    sx={{display: "inline-block", width: "max-content"}}
                >
                    {row.title}
                </SoftTypography>
            ),
        },
        {
            name: "Message",
            value: "body",
            align: "left",
            width: "40",
            renderCell: (row) => (
                <SoftTypography variant="caption" color="text" sx={{display: "inline-block"}}>
                    {row.body}
                </SoftTypography>
            ),
        },
        {
            name: "Type",
            value: "type",
            align: "center",
            width: "10",
            renderCell: (row) => (
                <SoftTypography variant="caption" color="text" textTransform="uppercase">
                    {row.type}
                </SoftTypography>
            ),
        },
        {
            name: "Received",
            value: "createdAt",
            align: "center",
            width: "15",
            renderCell: (row) => (
                <SoftTypography variant="caption" color="text">
                    {formatDate(row)}
                    {row?.timezone ? ` (${row.timezone})` : ""}
                </SoftTypography>
            ),
        },
        {
            name: "Action",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) =>
                row.status === "UNREAD" ? (
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleMarkOne(row)}
                        title="Mark as read"
                    >
                        <CircleIcon sx={{fontSize: 14}} />
                    </IconButton>
                ) : (
                    <SoftBox />
                ),
        },
    ];

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox
                    pt={2}
                    px={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    gap={2}
                >
                    <SoftBox display="flex" alignItems="center" gap={2} flexShrink={0}>
                        <SoftTypography variant="h6" fontWeight="medium">
                            Notifications
                        </SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" gap={1} alignItems="center" flexShrink={0}>
                        <SoftButton
                            variant={unreadOnly ? "gradient" : "outlined"}
                            color="primary"
                            size="small"
                            onClick={() => setUnreadOnly((v) => !v)}
                        >
                            {unreadOnly ? "Showing unread" : "All"}
                        </SoftButton>
                        <SoftButton
                            variant="gradient"
                            color="primary"
                            size="small"
                            onClick={handleMarkAll}
                            sx={{flexShrink: 0}}
                        >
                            Mark all read
                        </SoftButton>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <EnhancedTable
                            TableComponent={DataTable}
                            isLoading={isLoading}
                            data={items}
                            loadingMessage="Loading notifications..."
                            noDataMessage={unreadOnly ? "No unread notifications" : "No notifications yet"}
                            columns={columns}
                        />
                    </Grid>
                </Grid>
            </SoftBox>
        </DashboardLayout>
    );
}

export default Notifications;
